import { useState, useCallback } from 'react';
import { CustomResource } from '../../../types/CustomResource';
import { InitiativesStatusApi } from './useInitiativesStatus';
import { PLAN_TYPE_ID_BY_FIELD } from '../constants';

export type CustomResourcesApi = {
  customResources: CustomResource[] | null;
  setResources: ({ resources }: { resources: CustomResource[] }) => void;
  prepareCustomResourcesPayload: ({
    storedCustomResources,
    getActionedItemValueById,
    selectedItems,
  }: {
    storedCustomResources?: CustomResource[] | null;
    getActionedItemValueById?: InitiativesStatusApi['getActionedItemValueById'];
    selectedItems?: Record<string, string[]>;
  }) => CustomResource[];
  getResourcesByPlanTypeAndCategory: ({
    resources,
    actionPlanType,
    category,
  }: {
    resources: CustomResource[];
    actionPlanType: CustomResource['actionPlanType'];
    category: CustomResource['crCategory'];
  }) => CustomResource[];
  createNewResource: ({
    actionPlanType,
    category,
  }: {
    actionPlanType: CustomResource['actionPlanType'];
    category: CustomResource['crCategory'];
  }) => void;
  updateResourceFieldByUuid: <T extends keyof CustomResource>({
    uuid,
    field,
    value,
  }: {
    uuid: string;
    field: T;
    value: CustomResource[T];
  }) => void;
  getPersistentResources: ({ resources }: { resources: CustomResource[] }) => CustomResource[];
  getTransientResources: ({ resources }: { resources: CustomResource[] }) => CustomResource[];
};

export const LOCAL_RESOURCE_ID_PREFIX = 'NEW-RESOURCE-';

export const useCustomResources = (): CustomResourcesApi => {
  const [customResources, setCustomResources] = useState<CustomResource[] | null>(null);

  const setResources: CustomResourcesApi['setResources'] = useCallback(({ resources }) => {
    if (resources)
      setCustomResources(
        resources.map((resource) => {
          return resource.crUuid
            ? resource
            : {
                ...resource,
                crUuid: `${Date.now().toString(36)}-${crypto.randomUUID()}-${resource.crCategory}-${resource.actionPlanType}`,
              };
        }),
      );
  }, []);

  const createNewResource: CustomResourcesApi['createNewResource'] = useCallback(
    ({ actionPlanType, category }) => {
      setCustomResources((prev) => [
        ...prev,
        {
          crUuid: `${LOCAL_RESOURCE_ID_PREFIX}${Date.now().toString(36)}-${crypto.randomUUID()}-${category}-${actionPlanType}`,
          crCategory: category,
          actionPlanType: actionPlanType,
          crIsIncluded: false,
        },
      ]);
    },
    [],
  );

  const updateResourceFieldByUuid: CustomResourcesApi['updateResourceFieldByUuid'] = useCallback(
    <T extends keyof CustomResource>({
      uuid,
      field,
      value,
    }: {
      uuid: string;
      field: T;
      value: CustomResource[T];
    }) => {
      setCustomResources((prev) => {
        return prev.map((r) => (r.crUuid === uuid ? { ...r, [field]: value } : r));
      });
    },
    [],
  );

  const prepareCustomResourcesPayload: CustomResourcesApi['prepareCustomResourcesPayload'] =
    useCallback(
      ({ getActionedItemValueById, storedCustomResources, selectedItems }) => {
        const isCompleted = !!getActionedItemValueById;

        const customUuids = new Set(
          customResources?.map((item) => item.crUuid?.replace(LOCAL_RESOURCE_ID_PREFIX, '')) ?? [],
        );

        const baseList = isCompleted
          ? []
          : (storedCustomResources ?? []).filter((resource) => !customUuids.has(resource.crUuid));

        const result = customResources?.reduce<CustomResource[]>(
          (acc, { isChecked, ...resource }) => {
            // Needed so in case of 'back' button and then 'next' button again flow we were able to correctly save custom resource
            // that was selected but after saving lost their isChecked field(as we cut isChecked from the payload)
            const isSelected =
              selectedItems &&
              !!selectedItems[PLAN_TYPE_ID_BY_FIELD[resource.actionPlanType]]?.find(
                (selectedItem) => {
                  if (!selectedItem || !resource.crUuid) return false;

                  return (
                    selectedItem?.replace(LOCAL_RESOURCE_ID_PREFIX, '') ===
                    resource.crUuid?.replace(LOCAL_RESOURCE_ID_PREFIX, '')
                  );
                },
              );

            if (
              (isCompleted || isChecked || isSelected) &&
              resource.actionPlanType &&
              resource.crTitle
            ) {
              const normalizedUuid = resource.crUuid?.replace(LOCAL_RESOURCE_ID_PREFIX, '');

              const resourcePayload: CustomResource = {
                ...resource,
                crUuid: normalizedUuid,
                crNote: resource.crNote || null,
                crDueDate: resource.crDueDate || null,
                crPriority: resource.crPriority || null,
              };

              if (getActionedItemValueById) {
                resourcePayload.crIsIncluded = getActionedItemValueById(resource.crUuid);
              }

              acc.push(resourcePayload);
            }

            return acc;
          },
          baseList,
        );

        return result;
      },
      [customResources],
    );

  // Get resources that already exist in the database
  const getPersistentResources: CustomResourcesApi['getPersistentResources'] = ({ resources }) => {
    return resources?.filter((resource) => !resource.crUuid.includes(LOCAL_RESOURCE_ID_PREFIX));
  };

  // Get newly created resources that are not yet saved in the database
  const getTransientResources: CustomResourcesApi['getTransientResources'] = ({ resources }) => {
    return resources?.filter((resource) => resource.crUuid.includes(LOCAL_RESOURCE_ID_PREFIX));
  };

  const getResourcesByPlanTypeAndCategory: CustomResourcesApi['getResourcesByPlanTypeAndCategory'] =
    ({ resources, actionPlanType, category }) => {
      return resources?.filter(
        (resource) =>
          resource.crCategory === category && resource.actionPlanType === actionPlanType,
      );
    };

  return {
    customResources,
    setResources,
    prepareCustomResourcesPayload,
    createNewResource,
    updateResourceFieldByUuid,
    getPersistentResources,
    getTransientResources,
    getResourcesByPlanTypeAndCategory,
  };
};
